import { Board, Tile, Position, TileSymbol } from './types';

/**
 * Generates a random, solvable board for Snap Pairs
 */

function positionToKey(pos: Position): string {
  return `${pos.row},${pos.col}`;
}

function createTile(symbol: TileSymbol, position: Position, index: number): Tile {
  return {
    id: `${symbol}-${index}`,
    symbol,
    position,
    matched: false
  };
}

/**
 * Shuffle array using Fisher-Yates algorithm
 */
function shuffle<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Generate all positions for a grid
 */
function generatePositions(gridSize: number): Position[] {
  const positions: Position[] = [];
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      positions.push({ row, col });
    }
  }
  return positions;
}

/**
 * Create pairs of symbols for the board
 */
function createSymbolPairs(gridSize: number, availableSymbols: TileSymbol[]): TileSymbol[] {
  const totalTiles = gridSize * gridSize;
  const pairCount = totalTiles / 2;

  if (totalTiles % 2 !== 0) {
    throw new Error('Grid size must result in an even number of tiles');
  }

  if (pairCount > availableSymbols.length) {
    throw new Error(`Not enough symbols for ${gridSize}x${gridSize} grid. Need ${pairCount}, have ${availableSymbols.length}`);
  }

  // Select random symbols and duplicate them
  const selectedSymbols = shuffle(availableSymbols).slice(0, pairCount);
  const symbolPairs: TileSymbol[] = [];

  selectedSymbols.forEach(symbol => {
    symbolPairs.push(symbol, symbol);
  });

  return shuffle(symbolPairs);
}

/**
 * Generate a random board
 */
export function generateBoard(gridSize: number, symbols: TileSymbol[]): Board {
  const positions = generatePositions(gridSize);
  const symbolPairs = createSymbolPairs(gridSize, symbols);
  const tiles = new Map<string, Tile>();

  positions.forEach((position, index) => {
    const symbol = symbolPairs[index];
    const tile = createTile(symbol, position, index);
    tiles.set(positionToKey(position), tile);
  });

  return {
    tiles,
    gridSize
  };
}

/**
 * Get tile at specific position
 */
export function getTileAt(board: Board, position: Position): Tile | undefined {
  return board.tiles.get(positionToKey(position));
}

/**
 * Check if a tile is free (has at least one open side)
 */
export function isTileFree(board: Board, tile: Tile): boolean {
  const { row, col } = tile.position;

  // Check all four sides - ignore matched tiles
  const leftTile = col > 0 ? getTileAt(board, { row, col: col - 1 }) : undefined;
  const hasLeftNeighbor = leftTile !== undefined && !leftTile.matched;

  const rightTile = col < board.gridSize - 1 ? getTileAt(board, { row, col: col + 1 }) : undefined;
  const hasRightNeighbor = rightTile !== undefined && !rightTile.matched;

  const topTile = row > 0 ? getTileAt(board, { row: row - 1, col }) : undefined;
  const hasTopNeighbor = topTile !== undefined && !topTile.matched;

  const bottomTile = row < board.gridSize - 1 ? getTileAt(board, { row: row + 1, col }) : undefined;
  const hasBottomNeighbor = bottomTile !== undefined && !bottomTile.matched;

  // Tile is free if at least one side is open
  return !hasLeftNeighbor || !hasRightNeighbor || !hasTopNeighbor || !hasBottomNeighbor;
}

/**
 * Find all tiles with a specific symbol
 */
export function findTilesBySymbol(board: Board, symbol: TileSymbol): Tile[] {
  const result: Tile[] = [];
  board.tiles.forEach(tile => {
    if (tile.symbol === symbol && !tile.matched) {
      result.push(tile);
    }
  });
  return result;
}

/**
 * Check if two tiles can be matched
 */
export function canMatch(board: Board, tile1: Tile, tile2: Tile): boolean {
  if (tile1.id === tile2.id) return false;
  if (tile1.symbol !== tile2.symbol) return false;
  if (tile1.matched || tile2.matched) return false;

  return isTileFree(board, tile1) && isTileFree(board, tile2);
}

/**
 * Get all valid matches for a given tile
 */
export function getValidMatches(board: Board, tile: Tile): Tile[] {
  if (!isTileFree(board, tile) || tile.matched) {
    return [];
  }

  const sameSymbolTiles = findTilesBySymbol(board, tile.symbol);

  return sameSymbolTiles.filter(otherTile =>
    otherTile.id !== tile.id && canMatch(board, tile, otherTile)
  );
}

/**
 * Remove matched tiles from the board
 */
export function removeTiles(board: Board, tile1: Tile, tile2: Tile): Board {
  const newTiles = new Map(board.tiles);

  // Mark tiles as matched instead of removing them (for now, static mode)
  const updatedTile1 = { ...tile1, matched: true };
  const updatedTile2 = { ...tile2, matched: true };

  newTiles.set(positionToKey(tile1.position), updatedTile1);
  newTiles.set(positionToKey(tile2.position), updatedTile2);

  return {
    ...board,
    tiles: newTiles
  };
}

/**
 * Count remaining unmatched tiles
 */
export function getRemainingTileCount(board: Board): number {
  let count = 0;
  board.tiles.forEach(tile => {
    if (!tile.matched) count++;
  });
  return count;
}

/**
 * Check if the board is cleared
 */
export function isBoardCleared(board: Board): boolean {
  return getRemainingTileCount(board) === 0;
}

/**
 * Find a valid matching pair for hint
 */
export function findValidPair(board: Board): [Tile, Tile] | null {
  const unmatched: Tile[] = [];
  board.tiles.forEach(tile => {
    if (!tile.matched) {
      unmatched.push(tile);
    }
  });

  for (const tile1 of unmatched) {
    if (!isTileFree(board, tile1)) continue;

    for (const tile2 of unmatched) {
      if (tile1.id !== tile2.id && canMatch(board, tile1, tile2)) {
        return [tile1, tile2];
      }
    }
  }

  return null;
}

/**
 * Check if any valid matches exist on the board
 */
export function hasValidMatches(board: Board): boolean {
  return findValidPair(board) !== null;
}

/**
 * Shuffle remaining unmatched tiles on the board
 */
export function shuffleRemainingTiles(board: Board): Board {
  const unmatched: Tile[] = [];
  const positions: Position[] = [];

  board.tiles.forEach(tile => {
    if (!tile.matched) {
      unmatched.push(tile);
      positions.push(tile.position);
    }
  });

  const shuffledPositions = shuffle(positions);

  // Start with only matched tiles
  const newTiles = new Map<string, Tile>();
  board.tiles.forEach(tile => {
    if (tile.matched) {
      newTiles.set(positionToKey(tile.position), tile);
    }
  });

  // Add unmatched tiles to their new shuffled positions
  unmatched.forEach((tile, index) => {
    const newPosition = shuffledPositions[index];

    const updatedTile = {
      ...tile,
      position: newPosition
    };

    newTiles.set(positionToKey(newPosition), updatedTile);
  });

  return {
    ...board,
    tiles: newTiles
  };
}

/**
 * Regenerate matched tiles with new random symbols (for infinity mode)
 */
export function regenerateMatchedTiles(board: Board, tile1: Tile, tile2: Tile, availableSymbols: TileSymbol[]): Board {
  const newTiles = new Map(board.tiles);

  // Generate two new random symbols
  const newSymbol1 = availableSymbols[Math.floor(Math.random() * availableSymbols.length)];
  const newSymbol2 = availableSymbols[Math.floor(Math.random() * availableSymbols.length)];

  // Replace tile1 with new tile
  const newTile1: Tile = {
    id: `${newSymbol1}-${Date.now()}-1`,
    symbol: newSymbol1,
    position: tile1.position,
    matched: false
  };

  // Replace tile2 with new tile
  const newTile2: Tile = {
    id: `${newSymbol2}-${Date.now()}-2`,
    symbol: newSymbol2,
    position: tile2.position,
    matched: false
  };

  newTiles.set(positionToKey(tile1.position), newTile1);
  newTiles.set(positionToKey(tile2.position), newTile2);

  return {
    ...board,
    tiles: newTiles
  };
}
